import { createContext, ErrorInfo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AvailabilityType, IAvailability, IBookableresourceAvailability, IBookableresourcecategoryResponse, ICharacteristic, IContractedHours, IExpandCalendarAvailabity, IExpandCalendarResponse, IResource, IResourceIngress, RepeatPattern } from "../interfaces/resource";
import { IProviderProps } from "../interfaces/provider";
import { apiContext } from "./api";
import { authContext } from "./auth";
import { EEventStatus } from "../helpers/calendar";
import { renderGenericFallbackUi } from "../components/error-boundary/generic-fallback-ui";
import { Icon } from "@fluentui/react";
import { addDays, addMinutes, addMonths, differenceInDays, differenceInMinutes, endOfDay, format, isBefore, isSameDay, startOfDay, subDays, subMinutes, subMonths } from "date-fns";
import { IEvent, IEventBase } from "../interfaces/event";
import { availabilityFormatter, availabilityFortnightlyFormatter, contractedHoursFormatter, leaveFormatter } from "../helpers/shifts/shiftsFormatter";

interface IResourceContext {
    loading: string
    getResource: (id: string) => IResource | null
    getAllResources: () => IResource[]
    getResourceCount: () => number
    setResources: (resources: IResource[]) => void
    updateAvailability: (availability: IAvailability[]) => void
    getAvailabilities: () => IAvailability[]
    getAvailabilityByResource: (resourceId: string) => EEventStatus;
    getLeaves(): IEvent[];
    getIlluminaAvailablities: (start: Date, end: Date) => IEvent[];
    ensureAvailabilityRange: (start: Date, end: Date) => Promise<void>;
}

const resourcesContext = createContext<IResourceContext>({
    loading: '',
    getAllResources: () => ([]),
    getResource: () => null,
    getResourceCount: () => 0,
    setResources: (resources: IResource[]) => { },
    updateAvailability: (availability: IAvailability[]) => { },
    getAvailabilities: () => [],
    getAvailabilityByResource: (resourceId: string) => (EEventStatus.error),
    getLeaves: () => ([]),
    getIlluminaAvailablities: () => ([]),
    ensureAvailabilityRange: async () => { }
})

const ResourceProvider = (props: IProviderProps) => {
    const [resources, setResources] = useState<IResource[]>()
    const [availabilityMap, setAvailabilityMap] = useState<Map<string, EEventStatus>>(new Map())
    const [loading, setLoading] = useState<string>('Resources')
    const { get, post } = useContext(apiContext)
    const { isAuthed } = useContext(authContext)
    const [loadError, setLoadError] = useState(false)
    const [leaveStore, setLeaveStore] = useState<IEvent[]>([]);
    const [illuminaAvailability, setIlluminaAvailablity] = useState<IEvent[]>([]);
    const [loadedRange, setLoadedRange] = useState<{ start: Date, end: Date }>();
    const [cycleLength, setCycleLength] = useState<number>(14);

    const getBetweenFilter = (sd: Date, ed: Date, startColName: string, endColName: string): string => {
        //add/remove a day to include boundaries events
        var sdStr = format(sd, 'yyyy-MM-dd')
        var edStr = format(ed, 'yyyy-MM-dd')
        //https://forums.asp.net/t/1912609.aspx?Validate+overlapping+of+two+date+range
        let filter = `Microsoft.Dynamics.CRM.Between(PropertyName='${startColName}',PropertyValues=["${sdStr}","${edStr}"])`
        filter += ` or Microsoft.Dynamics.CRM.Between(PropertyName='${endColName}',PropertyValues=["${sdStr}","${edStr}"])`;
        filter += ` or (${startColName} le ${sdStr} and ${endColName} ge ${edStr})`;
        return `(${filter})`;
    }

    const loadResources = useCallback(async (start: Date, end: Date) => {
        console.log('loadResources called', start, end);
            try {
                const newResources: IResource[] = []
                const siteId = localStorage.getItem('siteid');

                //load leave requests for past and future six months
                let today = new Date();
                let leaveFilter = getBetweenFilter(subMonths(today, 6), addMonths(today, 6), 'msdyn_starttime', 'msdyn_endtime');
                let availabilityFilter = getBetweenFilter(start, end, 'illumina_startdate', 'illumina_enddate')
                const startDate = start;
                const endDate = end;
                if (!siteId) {
                    // handel error
                }
                const resourceCategories: { value: IBookableresourcecategoryResponse[] } = await get('/Entity/bookableresourcecategories', {
                    select: 'name,description,illumina_displaycolour',
                    filter: 'illumina_displaycolour ne null'
                });

                const req: { value: IResourceIngress[] } = await get('/Entity/bookableresources',
                    {
                        select: 'name,illumina_resourcesummary, resourcetype,illumina_minimumworkhourperpayroll,illumina_maximumhourspershift,_calendarid_value',
                        orderby: 'lastname desc',
                        filter: `statecode eq 0 and resourcetype eq 3 and illumina_BookableResource_illumina_sitelocation_il/any(o:o/illumina_sitelocationid eq '${siteId}')`,
                        expand: `UserId($select=firstname,lastname,photourl),illumina_availability_BookableResource_bookableresource($filter=${availabilityFilter} and statuscode eq 390950002),` + //390950002 means approved
                            'bookableresource_bookableresourcecharacteristic_Resource($select=name,bookableresourcecharacteristicid,_ratingvalue_value,illumina_expirydate,_characteristic_value,statecode;$filter=statecode eq 0),' +
                            'illumina_BookableResource_illumina_sitelocation_il($select=illumina_name;$filter=illumina_sitelocationid eq ' + siteId + '),' +
                            'bookableresource_bookableresourcecategoryassn_Resource($select=_resourcecategory_value),' +
                            `msdyn_bookableresource_msdyn_timeoffrequest_Resource($filter=statecode eq 1 and _msdyn_approvedby_value ne null and (${leaveFilter}))`
                    })

                let illuminaAvailabilities: IEvent[] = [];
                
                req.value.forEach((res) => {
                    if (!res.illumina_BookableResource_illumina_sitelocation_il.find(location => location.illumina_sitelocationid === siteId)) return;

                    let resource = {
                        personalNote: res.illumina_resourcesummary,
                        title: res.name,
                        id: res.bookableresourceid,
                        profileImageUrl: res.UserId?.photoUrl,
                        minimumWorkHoursPerPayroll: res.illumina_minimumworkhourperpayroll as number,
                        maximumWorkHourPerPayroll: res.illumina_maximumworkhourperpayroll as number,
                        maxHoursPerShift: res.illumina_maximumhourspershift,
                        calendarId: res._calendarid_value,
                        characteristics: res.bookableresource_bookableresourcecharacteristic_Resource
                            .map((ch): ICharacteristic => ({
                                name: ch.name,
                                id: ch.bookableresourcecharacteristicid,
                                ratingName: ch['_ratingvalue_value@OData.Community.Display.V1.FormattedValue'],
                                expiryDate: ch.illumina_expirydate,
                                characteristicId: ch['_characteristic_value']
                            })).sort((x, y) => x.name.localeCompare(y.name)),
                        siteLocation: res.illumina_BookableResource_illumina_sitelocation_il,
                        index: newResources.length,
                        categories: [],
                        approvedLeaves: []
                    } as IResource;

                    let availabilityContractedHours: IContractedHours[] = [];
                    const brAvailabilities: IEvent[] = [];

                    // Filter availabilities by date range
                    res.illumina_availability_BookableResource_bookableresource
                        .filter(y => {
                            const yStart = new Date(y.illumina_startdate);
                            const yEnd = new Date(y.illumina_enddate);
                            return yEnd >= start && yStart <= end;
                        })
                        .forEach(y => {
                            if (y.illumina_availabilitytype === AvailabilityType.ContractedHours) {
                                availabilityContractedHours = availabilityContractedHours.concat(contractedHoursFormatter(y));
                            } else {
                                if (y.illumina_repeatpattern === RepeatPattern.Fortnightly) {
                                    brAvailabilities.push(...availabilityFortnightlyFormatter(resource.id, y));
                                } else {
                                    brAvailabilities.push(availabilityFormatter(resource.id, y));
                                }
                            }
                        });

                    if (availabilityContractedHours.length > 0) {
                        const contractedHoursEvents = groupAndCreateEvents(availabilityContractedHours, resource.id);
                        if (contractedHoursEvents.length > 0) {
                            illuminaAvailabilities = illuminaAvailabilities.concat(contractedHoursEvents);
                        }
                    }

                    if (brAvailabilities.length > 0)
                        illuminaAvailabilities = illuminaAvailabilities.concat(brAvailabilities);
                    newResources.push(resource);
                    res.bookableresource_bookableresourcecategoryassn_Resource?.forEach(x => {
                        let rc = resourceCategories.value.find(y => y.bookableresourcecategoryid == x._resourcecategory_value);
                        if (rc != null)
                            resource.categories.push({
                                description: rc.description,
                                displayColour: rc.illumina_displaycolour,
                                id: rc.bookableresourcecategoryid,
                                name: rc.name
                            });
                    });
                    res.msdyn_bookableresource_msdyn_timeoffrequest_Resource.forEach(x => {
                        resource.approvedLeaves.push({ start: new Date(x.msdyn_starttime), end: new Date(x.msdyn_endtime), id: x.msdyn_timeoffrequestid });
                    })
                });
               
                setIlluminaAvailablity(illuminaAvailabilities);
          
                let leaves: IEvent[] = [];
                newResources.forEach(r => {
                    r.approvedLeaves.forEach(al => {
                        let e = leaveFormatter(al, r);
                        leaves.push(e);
                    })
                });
                setLeaveStore(leaves);

                newResources?.sort((a, b) => {
                    if (a.title > b.title) return 1;
                    if (a.title < b.title) return -1;
                    return 0
                }).forEach((resource, index) => {
                    resource.index = index;
                });



                setLoading('Calendars')

                const calendarIdToResource = new Map<string, IResource>();
                newResources.forEach(r => {
                    if (r.calendarId) {
                        calendarIdToResource.set(r.calendarId, r);
                    }
                });

                const expandCalendarRequest = {
                    calendarIds: Array.from(calendarIdToResource.keys()),
                    start: start,
                    end: end
                }
                const expandCalendarResponses: IExpandCalendarResponse[] = await post('/Rostering/expandcalendars', expandCalendarRequest);
                expandCalendarResponses.forEach(r => {
                    const br = calendarIdToResource.get(r.calendarId);
                    if (br) {
                        br.availabilities = r.availabilities.map((x: IExpandCalendarAvailabity) => ({
                            ...x,
                            start: new Date(x.start),
                            end: new Date(x.end)
                        }));
                    }
                })

                newResources.push({
                    title: "Unassigned",
                    id: "0",
                    index: newResources.length,
                    characteristics: [],
                    categories: [],
                    approvedLeaves: []
                })
                setResources(newResources)
                setLoading('')
            } catch (e) {
                setLoadError(true)
            }
        }, [get, post])

    const hasLoadedResources = useRef(false);

    useEffect(() => {
        if (!isAuthed || hasLoadedResources.current) {
            return;
        }
        hasLoadedResources.current = true;
        const siteId = localStorage.getItem('siteid');
        const localPayrollData = localStorage.getItem(`illuminance-scheduler-payroll-data_${siteId}`);
        let payrollStart = startOfDay(new Date());
        let payrollEnd = endOfDay(addDays(payrollStart, 13));
        if (localPayrollData) {
            const payrollData = JSON.parse(localPayrollData) as { start: string, end: string };
            const psd = new Date(payrollData.start);
            const ped = new Date(payrollData.end);
            const pLength = differenceInDays(ped, psd);
            setCycleLength(pLength);
            payrollStart = subDays(psd, pLength);
            payrollEnd = addDays(ped, pLength * 2);
        } else {
            setCycleLength(14);
            payrollStart = subDays(payrollStart, 14);
            payrollEnd = addDays(payrollEnd, 28);
        }
        loadResources(payrollStart, payrollEnd).then(() => {
            setLoadedRange({ start: payrollStart, end: payrollEnd });
        });
    }, [isAuthed, loadResources])

    const getResource = (id: string) => {
        if (!resources) {
            return null
        }
        return resources.find(res => res.id === id) ?? null
    }
    const getResources = useCallback(() => {
        return resources ?? []
    }, [resources])

    const getLeaves = useCallback(() => {
        return leaveStore ?? []
    }, [resources])


    const getResourceCount = (): number => {
        return resources ? resources.length : 0
    }

    const overrideResources = (nResources: IResource[]) => {
        setResources(nResources)
    }

    const updateAvailability = (availability: IAvailability[]) => {
        setAvailabilityMap(prev => {
            const map = new Map(prev);
            availability.forEach(a => map.set(a.resourceId, a.availability));
            return map;
        });
    }

    const getAvailabilities = useCallback(() => {
        return Array.from(availabilityMap.entries()).map(([resourceId, availability]) => ({ resourceId, availability }));
    }, [availabilityMap])

    const getAvailabilityByResource = (resourceId: string): EEventStatus => {
        return availabilityMap.get(resourceId) ?? EEventStatus.valid
    }
    const getIlluminaAvailablities = useCallback((start: Date, end: Date): IEvent[] => {
        return (illuminaAvailability ?? []).filter(av => av.start >= start && av.end <= end);
    }, [illuminaAvailability]);

    const ensureAvailabilityRange = useCallback(async (start: Date, end: Date) => {
        if (!loadedRange) return;
        if (start >= loadedRange.start && end <= loadedRange.end) return;
        const newStart = subDays(start, cycleLength);
        const newEnd = addDays(end, cycleLength * 2);
        await loadResources(newStart, newEnd);
        setLoadedRange({ start: newStart, end: newEnd });
    }, [loadedRange, cycleLength, loadResources]);

    function groupAndCreateEvents(data: IContractedHours[], sourceId: string) {
        const groupedByDay = {};
        data.forEach(item => {
          const startDate = new Date(item.startDate);
          const endDate = new Date(item.endDate);
      
          const dayKey = format(startDate, 'yyyy-MM-dd'); 
      
          // @ts-ignore
          if (!groupedByDay[dayKey]) {
            const start = startOfDay(startDate);
            const end = addMinutes(start, 30);
            // @ts-ignore
            groupedByDay[dayKey] = {
              start: start,
              end: end,
              id: item.id,
              editable: false,
              type: item.type,
              resourceEditable: false,
              events: [],
            };
          }
      
          // @ts-ignore
          groupedByDay[dayKey].events.push({
            ... item
          });
        });
      
        const events = Object.keys(groupedByDay).map(dayKey => {
        // @ts-ignore
          const dayData = groupedByDay[dayKey];
          let event = {
            resourceId: sourceId,
            title: `contractedHours`,
            id: dayData.id,
            display: 'contractedHours',
            description: '',
            editable: false,
            start: dayData.start,
            end: dayData.end,
            availabilityType: dayData.type,
            resourceEditable: false,
            extendedProps: {
                events: dayData.events 
            },
        }  as unknown as IEvent;
        return event;   
      });
      return events;
    }

    return (
        <resourcesContext.Provider value={{
            loading,
            getResource,
            getResourceCount,
            getAllResources: getResources,
            setResources: overrideResources,
            getAvailabilities,
            updateAvailability,
            getAvailabilityByResource,
            getLeaves,
            getIlluminaAvailablities,
            ensureAvailabilityRange
        }}>
            {loadError ? (
                <div style={{
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    color: 'rgb(243, 112, 55)',
                    fontSize: '12px'
                }}>
                    <Icon iconName="error" style={{ fontSize: '24px' }} />
                    <p style={{ marginBottom: '0' }}>An error occurred whilst loading {loading.toLowerCase()}.</p>
                    <p>
                        Please <a
                            onClick={(e) => { e.preventDefault(); window.location.reload() }}
                            href={window.location.href}
                            style={{ color: 'rgb(243, 112, 55)', fontWeight: '500' }}
                        >
                            reload
                        </a> the page.
                    </p>
                </div>
            ) : (
                props.children
            )}
        </resourcesContext.Provider>
    )
}

export { ResourceProvider, resourcesContext }
